import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Plug,
  CheckCircle2,
  XCircle,
  Trash2,
  Pencil,
  Save,
  X,
  Loader2,
  AlertCircle,
  Star,
  Database,
} from 'lucide-react';
import { apiFetch } from './apiBase';
import CatalogInstall from './CatalogInstall';

const EMPTY_DRAFT = {
  name: '',
  description: '',
  baseUrl: '',
  username: '',
  password: '',
  rejectUnauthorized: true,
  aiSqlRunnerReportPath: '',
  environmentLabel: '',
};

function StatusPill({ status, message }) {
  if (status == null) return <span className="text-xs text-slate-400">Not tested</span>;
  const ok = status === 200;
  const Icon = ok ? CheckCircle2 : XCircle;
  const cls = ok ? 'text-emerald-400' : 'text-rose-400';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${cls}`}
      title={message || (ok ? '' : `HTTP ${status}`)}
    >
      <Icon className="h-3 w-3" />
      {ok ? 'OK' : 'Failed'}
    </span>
  );
}

function DraftForm({ draft, setDraft, saving, onSave, onCancel, isNew, onCheck, checkBusy, checkResult }) {
  return (
    <div className="space-y-3 rounded-lg border border-cyan-700/40 bg-cyan-950/20 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="font-medium text-slate-300">Name *</span>
          <input
            className="input-field"
            placeholder="Dev1, Dev2, Production…"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-300">Environment</span>
          <select
            className="select-field-sm mt-1.5 block w-full text-sm"
            value={draft.environmentLabel}
            onChange={(e) => setDraft({ ...draft, environmentLabel: e.target.value })}
          >
            <option value="">—</option>
            <option value="dev">Dev</option>
            <option value="uat">UAT</option>
            <option value="non-production">Non-production</option>
            <option value="production">Production</option>
          </select>
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="font-medium text-slate-300">Oracle HCM URL *</span>
          <input
            className="input-field font-mono text-xs"
            placeholder="https://your-tenant.login.oraclecloud.com"
            value={draft.baseUrl}
            onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })}
          />
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-300">Username *</span>
          <input
            className="input-field"
            value={draft.username}
            onChange={(e) => setDraft({ ...draft, username: e.target.value })}
          />
        </label>
        <label className="text-sm">
          <span className="font-medium text-slate-300">
            Password {isNew ? '*' : '(blank = keep)'}
          </span>
          <input
            type="password"
            className="input-field"
            value={draft.password}
            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="font-medium text-slate-300">Description</span>
          <input
            className="input-field"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="font-medium text-slate-300">Custom report path (optional)</span>
          <input
            className="input-field font-mono text-xs"
            value={draft.aiSqlRunnerReportPath}
            onChange={(e) => setDraft({ ...draft, aiSqlRunnerReportPath: e.target.value })}
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-400">
        <input
          type="checkbox"
          checked={draft.rejectUnauthorized}
          onChange={(e) => setDraft({ ...draft, rejectUnauthorized: e.target.checked })}
        />
        Verify secure connection
      </label>
      {checkResult && (
        <p
          className={`text-sm ${checkResult.success ? 'text-emerald-300' : 'text-rose-300'}`}
        >
          {checkResult.msg || checkResult.message}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-lg bg-cyan-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-800 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
        <button type="button" onClick={onCheck} disabled={checkBusy} className="btn-secondary text-sm">
          {checkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
          Test credentials
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </div>
  );
}

export default function Connections({ onActiveChanged }) {
  const [connections, setConnections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mongoMissing, setMongoMissing] = useState(false);
  const [devBypassBlock, setDevBypassBlock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [savingDraft, setSavingDraft] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [checkDraftBusy, setCheckDraftBusy] = useState(false);
  const [checkDraftResult, setCheckDraftResult] = useState(null);

  async function reload() {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/connections');
      setMongoMissing(false);
      setDevBypassBlock(false);
      const list = res.connections || [];
      setConnections(list);
      const active = list.find((c) => c.isActive);
      setActiveId(active ? active.id : null);
    } catch (e) {
      if (e.status === 503 && /sign in|real account/i.test(e.message || '')) {
        setDevBypassBlock(true);
      } else if (e.status === 503 || /saved connections are unavailable/i.test(e.message || '')) {
        setMongoMissing(true);
      } else {
        setError(e.message);
      }
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function checkDraft() {
    setCheckDraftBusy(true);
    setCheckDraftResult(null);
    try {
      if (draftId === 'new' || draft.password) {
        if (!draft.baseUrl || !draft.username || !draft.password) {
          setCheckDraftResult({ success: false, msg: 'Enter URL, username, and password.' });
          return;
        }
        const data = await apiFetch('/api/hcm/check-credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            baseUrl: draft.baseUrl,
            username: draft.username,
            password: draft.password,
            rejectUnauthorized: draft.rejectUnauthorized,
          }),
        });
        setCheckDraftResult(data);
      } else {
        const data = await apiFetch(`/api/connections/${draftId}/check-credentials`, {
          method: 'POST',
        });
        setCheckDraftResult(data);
      }
    } catch (e) {
      setCheckDraftResult({ success: false, msg: e.message });
    } finally {
      setCheckDraftBusy(false);
    }
  }

  async function saveDraft() {
    setSavingDraft(true);
    setError('');
    try {
      if (draftId === 'new') {
        await apiFetch('/api/connections', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(draft),
        });
      } else {
        const body = { ...draft };
        if (!body.password) delete body.password;
        await apiFetch(`/api/connections/${draftId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      setDraftId(null);
      setDraft(EMPTY_DRAFT);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingDraft(false);
    }
  }

  async function testConnection(c) {
    setTestingId(c.id);
    try {
      const data = await apiFetch(`/api/connections/${c.id}/test`, { method: 'POST' });
      setTestResults((prev) => ({ ...prev, [c.id]: data }));
      await reload();
    } catch (e) {
      setTestResults((prev) => ({ ...prev, [c.id]: { success: false, message: e.message } }));
    } finally {
      setTestingId(null);
    }
  }

  async function activate(c) {
    await apiFetch(`/api/connections/${c.id}/activate`, { method: 'POST' });
    setActiveId(c.id);
    await reload();
    onActiveChanged?.();
  }

  async function deactivate() {
    await apiFetch('/api/connections/deactivate', { method: 'POST' });
    setActiveId(null);
    await reload();
    onActiveChanged?.();
  }

  async function deleteConnection(c) {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await apiFetch(`/api/connections/${c.id}`, { method: 'DELETE' });
    await reload();
    onActiveChanged?.();
  }

  const sorted = useMemo(
    () => [...connections].sort((a, b) => a.name.localeCompare(b.name)),
    [connections]
  );

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading connections…
      </div>
    );
  }

  if (mongoMissing) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900">
        <div className="mb-2 flex items-center gap-2 font-semibold">
          <Database className="h-5 w-5" />
          Saved connections are unavailable
        </div>
        <p className="mb-2">
          Ask your administrator to enable saved tenants. You can still use the default
          Oracle connection below, if one is configured.
        </p>
        <EnvConnectionHint onActiveChanged={onActiveChanged} />
      </div>
    );
  }

  if (devBypassBlock) {
    return (
      <div className="space-y-4">
        <EnvConnectionHint onActiveChanged={onActiveChanged} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Connections</h2>
          <p className="mt-1 text-sm text-slate-400">
            Save Oracle tenants (Dev1, Dev2, …) and choose which one is active for reports and recon.
          </p>
        </div>
        <div className="flex gap-2">
          {activeId && (
            <button type="button" onClick={deactivate} className="btn-secondary text-sm">
              Use default connection
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setDraftId('new');
              setDraft(EMPTY_DRAFT);
            }}
            disabled={draftId !== null}
            className="inline-flex items-center gap-1 rounded-lg bg-cyan-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-800 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Add connection
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {draftId === 'new' && (
        <DraftForm
          draft={draft}
          setDraft={setDraft}
          saving={savingDraft}
          onSave={saveDraft}
          onCancel={() => {
            setDraftId(null);
            setDraft(EMPTY_DRAFT);
          }}
          isNew
          onCheck={checkDraft}
          checkBusy={checkDraftBusy}
          checkResult={checkDraftResult}
        />
      )}

      {sorted.length === 0 && draftId !== 'new' ? (
        <p className="card-panel border-dashed p-8 text-center text-sm text-slate-400">
          No saved connections. Add Dev1, Dev2, or your production tenant.
        </p>
      ) : (
        <div className="card-panel overflow-hidden">
          <table className="data-table w-full text-sm">
            <thead className="text-xs uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">URL</th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Test</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((c) =>
                draftId === c.id ? (
                  <tr key={c.id}>
                    <td colSpan={5} className="p-4">
                      <DraftForm
                        draft={draft}
                        setDraft={setDraft}
                        saving={savingDraft}
                        onSave={saveDraft}
                        onCancel={() => {
                          setDraftId(null);
                          setDraft(EMPTY_DRAFT);
                        }}
                        isNew={false}
                        onCheck={checkDraft}
                        checkBusy={checkDraftBusy}
                        checkResult={checkDraftResult}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id} className={c.isActive ? 'bg-emerald-50/50' : ''}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {c.isActive && (
                          <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" />
                        )}
                        <div>
                          <div className="font-medium">{c.name}</div>
                          {c.environmentLabel && (
                            <span className="text-xs text-slate-500">{c.environmentLabel}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs break-all">{c.baseUrl}</td>
                    <td className="px-4 py-2.5">{c.username}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={c.lastTestStatus} message={c.lastTestMessage} />
                      {testResults[c.id]?.message && (
                        <div className="mt-1 text-xs text-slate-400">{testResults[c.id].message}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => testConnection(c)}
                          disabled={testingId === c.id}
                          className="btn-secondary py-1 text-xs"
                        >
                          {testingId === c.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plug className="h-3 w-3" />
                          )}
                          Test
                        </button>
                        {c.isActive ? (
                          <span className="badge-success px-2 py-1">Active</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => activate(c)}
                            className="btn-primary py-1 text-xs"
                          >
                            Use this
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setDraftId(c.id);
                            setDraft({
                              ...EMPTY_DRAFT,
                              name: c.name,
                              description: c.description,
                              baseUrl: c.baseUrl,
                              username: c.username,
                              rejectUnauthorized: c.rejectUnauthorized,
                              aiSqlRunnerReportPath: c.aiSqlRunnerReportPath,
                              environmentLabel: c.environmentLabel,
                            });
                          }}
                          className="btn-secondary py-1 text-xs"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteConnection(c)}
                          className="btn-secondary py-1 text-xs text-rose-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      <EnvConnectionHint onActiveChanged={onActiveChanged} />

      <CatalogInstall activeConnection={sorted.find((c) => c.isActive) || null} />
    </div>
  );
}

function EnvConnectionHint({ onActiveChanged }) {
  const [conn, setConn] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    apiFetch('/api/hcm/connection')
      .then(setConn)
      .catch(() => setConn(null));
  }, []);

  async function testEnv() {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiFetch('/api/hcm/test', { method: 'POST' });
      setTestResult(data);
      onActiveChanged?.();
    } catch (e) {
      setTestResult({ success: false, message: e.message });
    } finally {
      setTesting(false);
    }
  }

  if (!conn) return null;

  const label =
    conn.source === 'db'
      ? conn.name
      : conn.isConfigured
        ? conn.description || conn.baseUrl?.replace(/^https?:\/\//, '') || 'Default connection'
        : 'Not configured';

  return (
    <div className="card-panel px-4 py-3 text-sm">
      <div className="font-medium text-slate-200">Currently connected to</div>
      {conn.isConfigured ? (
        <>
          <div className="mt-1 text-slate-300">
            <span className="font-semibold text-slate-100">{label}</span>
            {conn.source === 'env' && (
              <span className="ml-2 rounded bg-slate-700 px-1.5 py-0.5 text-xs text-slate-300">Default</span>
            )}
            {conn.source === 'db' && (
              <span className="ml-2 badge-success px-1.5 py-0.5 text-xs">
                saved
              </span>
            )}
          </div>
          <div className="truncate font-mono text-xs text-slate-500">{conn.baseUrl}</div>
          <div className="text-xs text-slate-500">{conn.username}</div>
        </>
      ) : (
        <p className="mt-1 text-amber-800">
          No active connection — add a tenant above and click Use this.
        </p>
      )}
      <button
        type="button"
        onClick={testEnv}
        disabled={testing || !conn.isConfigured}
        className="btn-secondary mt-2 text-xs"
      >
        {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
        Test active connection
      </button>
      {testResult?.message && (
        <p className={`mt-2 text-xs ${testResult.success ? 'text-emerald-700' : 'text-rose-700'}`}>
          {testResult.message}
        </p>
      )}
    </div>
  );
}
